/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, Stack, styled, useMediaQuery, useTheme } from '@mui/material';
import Typography from './Typography';
import TestIds from '../testIds';

const Title = styled(Typography)({
  '@media (max-width: 390px)': {
    fontSize: `calc(36 * calc(100vw / 390))`,
  },
});

const Text = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '4px',
  },
}));

const Inline = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

interface Props {
  title: string;
  statusChip: React.ReactNode;
  text?: React.ReactNode;
  drawdownButton?: React.ReactNode | React.ReactNode[];
}

export const StatusHeader = ({ title, statusChip, text, drawdownButton }: Props) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Stack
      width='100%'
      gap={'4px'}
      direction='row'
      data-testid={TestIds.components.statusHeader.container}
    >
      <Stack gap={'4px'} width='100%'>
        <Inline>
          <Title
            variant='display'
            color='primary500'
            data-testid={TestIds.components.statusHeader.title}
          >
            {title}
          </Title>
          <Box mt={-2}>{statusChip}</Box>
          {!isSm && <Stack ml={'auto'}>{drawdownButton}</Stack>}
        </Inline>
        {text && <Text data-testid={TestIds.components.statusHeader.text}>{text}</Text>}
      </Stack>
    </Stack>
  );
};
